import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { STAGE_LABEL } from "@/constants/roles";
import { useT } from "@/i18n/LanguageContext";
import { photoUrl, archivePhotoUrl, isImageFile } from "@/lib/photos";
import { toast } from "sonner";
import { X, Search, Download, Save, Trash2, RotateCcw, ChartColumn, Camera, Mic, MicOff, Send, Sparkles, ExternalLink, FileSpreadsheet } from "lucide-react";
import { ArchiveDialog } from "./ReportArchive";
import { FiltersV2 } from "./ReportFiltersV2";
import { ReportGrid } from "./ReportGrid";

const EMPTY = {
  front_cover_code: [], rear_cover_code: [], catalog_number: [], f_clamp_code: [], r_clamp_code: [], color: [], logo: [], spacer: [],
  brand: [], model: [], trim: [], rim_size: [], year_from: "", year_to: "", years: [], po: "",
  country: [], state: [], city: "",
  status: [], source: [], delivery: "", include_cancelled: true,
  customer: "", text: "", group_by: "", dataset: "both",
};
const STORE_KEY = "crm-report-state";
const LAST_ROW_KEY = "crm-report-last-row";

const ADVANCED_KEYS = ["f_clamp_code", "r_clamp_code", "trim", "color", "logo", "spacer", "rim_size", "country", "state", "city", "status", "source", "delivery", "customer"];

// Excel-style grid columns — every field the report knows about, per order line (no customer / stage columns, per user).
const COLS = [
  ["po_number", "PO #", 110], ["shopify_order_number", "Shopify #", 80], ["year", "Year", 55], ["brand", "Brand", 100], ["model", "Model", 110],
  ["trim", "Trim", 110], ["rim_size", "Rim", 50], ["vin", "VIN", 165], ["front_cover_code", "Front code", 95], ["f_clamp_code", "F-Clamp", 80],
  ["rear_cover_code", "Rear code", 95], ["r_clamp_code", "R-Clamp", 80], ["catalog_number", "Catalog #", 100], ["color", "Color", 130],
  ["logo", "Logo", 120], ["spacer", "Spacer", 80], ["spacer_qty", "Qty", 45], ["country", "Country", 80], ["state", "State", 60],
  ["city", "City", 110], ["source", "Source", 70], ["delivery", "Delivery", 75], ["tracking", "Tracking #", 150],
  ["created_at", "Created", 90], ["fulfilled_at", "Fulfilled", 90],
];

/** 📷 Quick photo preview for one row — live order (its Drive folder) or old-sheet row (gallery folder matched by PO). */
function PhotoPeek({ row, onClose, onOpenOrder }) {
  const arcId = row?.is_archive ? String(row.id).replace(/^arc:/, "") : null;
  const q = useQuery({
    queryKey: ["report-photos", row?.id],
    queryFn: async () => (await api.get(arcId ? `/report/archive-photos/${arcId}` : `/orders/${row.id}/drive-photos`)).data,
    enabled: !!row,
  });
  const files = (q.data?.files || []).filter(isImageFile);
  const url = (f, size) => (arcId ? archivePhotoUrl(arcId, f.id, size) : photoUrl(row.id, f.id, size));
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="rpt-photo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Camera size={18} /> {row?.is_archive ? `PO ${row?.po_number}` : row?.po_number} — photos
            {row?.is_archive && <Badge className="bg-emerald-600 text-white border-emerald-700 text-[9px] px-1 py-0 font-sans tracking-wide">OLD SHEET</Badge>}
            <span className="text-sm font-normal text-slate-500">{[row?.year, row?.brand, row?.model].filter(Boolean).join(" ")}</span>
            {row?.is_archive ? (
              q.data?.folder_url && <a href={q.data.folder_url} target="_blank" rel="noreferrer" className="ml-auto text-xs font-medium text-blue-700 hover:underline inline-flex items-center gap-1" title={q.data?.path} data-testid="rpt-photo-open-folder">Open Drive folder <ExternalLink size={12} /></a>
            ) : (
              <button type="button" onClick={onOpenOrder} className="ml-auto text-xs font-medium text-blue-700 hover:underline inline-flex items-center gap-1" data-testid="rpt-photo-open-order">
                Open order <ExternalLink size={12} />
              </button>
            )}
          </DialogTitle>
        </DialogHeader>
        {q.isLoading && <div className="text-sm text-slate-500 py-8 text-center">Loading photos…</div>}
        {q.isError && <div className="text-sm text-red-700 py-8 text-center">{q.error?.response?.data?.detail || "Could not load photos."}</div>}
        {!q.isLoading && !q.isError && files.length === 0 && (
          <div className="text-sm text-slate-500 py-8 text-center">
            {q.data?.error === "drive_not_connected" ? "Google Drive is not connected." : row?.is_archive ? "No photos in the matching gallery folder." : "No photos in this order's Drive folder."}
            {q.data?.folder_url && <div className="mt-2"><a className="text-blue-700 underline text-xs" href={q.data.folder_url} target="_blank" rel="noreferrer">Open Drive folder</a></div>}
          </div>
        )}
        {files.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[60vh] overflow-y-auto pr-1" data-testid="rpt-photo-grid">
            {files.map((f) => (
              <a key={f.id} href={url(f, "large")} target="_blank" rel="noreferrer" className="group block rounded-md overflow-hidden border border-slate-200 bg-slate-50" title={f.name}>
                <img src={url(f, "thumb")} alt={f.name} loading="lazy" className="h-32 w-full object-cover group-hover:opacity-90 transition-opacity" />
                <div className="px-1.5 py-1 text-[10px] text-slate-600 truncate">{f.name}</div>
              </a>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Right-hand assistant: type or dictate a question → filters applied + spoken-style reply. */
function AskPanel({ applied, onApply, onClose }) {
  const [msgs, setMsgs] = useState([{ role: "bot", text: "Ask me anything about the orders — e.g. “red Escalade covers shipped to Canada” or “how many orders by color in 2024”. Press the mic to dictate." }]);
  const [input, setInput] = useState("");
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = useMutation({
    mutationFn: async (question) => (await api.post("/report/ask", { question, current_filters: applied })).data,
    onSuccess: (d) => {
      setMsgs((m) => [...m, { role: "bot", text: d.reply }]);
      if (d.filters) onApply(d.filters);
    },
    onError: (e) => setMsgs((m) => [...m, { role: "bot", text: e?.response?.data?.detail || "Something went wrong — please try again.", error: true }]),
  });
  const send = (text) => {
    const q = (text ?? input).trim();
    if (!q || ask.isPending) return;
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setInput("");
    ask.mutate(q);
  };
  const toggleMic = () => {
    if (listening) { recRef.current?.stop(); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input needs Chrome or Edge (microphone access)."); return; }
    const rec = new SR();
    rec.lang = "en-US"; rec.interimResults = true; rec.continuous = false;
    let finalText = "";
    rec.onresult = (ev) => {
      let interim = "";
      for (const r of ev.results) { if (r.isFinal) finalText += r[0].transcript; else interim += r[0].transcript; }
      setInput(finalText || interim);
    };
    rec.onerror = (ev) => { setListening(false); toast.error(ev.error === "not-allowed" ? "Microphone blocked — allow it in the browser address bar." : `Voice error: ${ev.error}`); };
    rec.onend = () => { setListening(false); if (finalText.trim()) send(finalText); };
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  return (
    <aside className="rounded-xl border border-slate-200 bg-white flex flex-col h-[calc(100vh-11rem)] sticky top-4" data-testid="rpt-ask-panel">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <Sparkles size={16} className="text-violet-600" />
        <div className="font-semibold text-sm">Ask the report</div>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-slate-400">voice · text</span>
        <button type="button" onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-900" title="Hide the assistant (the report gets the full width)" data-testid="rpt-ask-close"><X size={14} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="rpt-ask-messages">
        {msgs.map((m, i) => (
          <div key={i} className={`max-w-[92%] rounded-lg px-3 py-2 text-[13px] leading-snug whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-slate-900 text-white" : m.error ? "bg-red-50 text-red-800 border border-red-200" : "bg-violet-50 text-slate-800 border border-violet-100"}`} data-testid={`rpt-ask-msg-${m.role}`}>
            {m.text.split(/(\*\*[^*]+\*\*)/g).map((part, j) => part.startsWith("**") ? <strong key={j}>{part.slice(2, -2)}</strong> : part)}
          </div>
        ))}
        {ask.isPending && <div className="text-xs text-slate-400 px-1 animate-pulse">Thinking…</div>}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-slate-200 flex items-center gap-2">
        <button
          type="button"
          onClick={toggleMic}
          data-testid="rpt-ask-mic"
          aria-pressed={listening}
          title={listening ? "Stop listening" : "Dictate your question"}
          className={`h-9 w-9 shrink-0 rounded-full inline-flex items-center justify-center border transition-colors ${listening ? "bg-red-600 border-red-700 text-white animate-pulse" : "bg-white border-slate-300 text-slate-700 hover:border-slate-900"}`}
        >
          {listening ? <MicOff size={16} /> : <Mic size={16} />}
        </button>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={listening ? "Listening…" : "Type a question…"}
          className="h-9 text-sm"
          data-testid="rpt-ask-input"
        />
        <Button size="sm" onClick={() => send()} disabled={!input.trim() || ask.isPending} className="h-9 bg-violet-600 hover:bg-violet-700 text-white" data-testid="rpt-ask-send">
          <Send size={14} />
        </Button>
      </div>
    </aside>
  );
}

export default function Report() {
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isSA = user.role === "super_admin";
  const restored = useMemo(() => {
    try { const s = JSON.parse(sessionStorage.getItem(STORE_KEY) || "null"); return s && typeof s === "object" ? { ...EMPTY, ...s } : EMPTY; } catch { return EMPTY; }
  }, []);
  const [f, setF] = useState(restored);
  const [applied, setApplied] = useState(restored);
  const [saveName, setSaveName] = useState("");
  const [peek, setPeek] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(() => ADVANCED_KEYS.some((k) => Array.isArray(restored[k]) ? restored[k].length : restored[k]));
  const [moreOpen, setMoreOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(() => localStorage.getItem("crm-report-ask-open") === "1");
  useEffect(() => { localStorage.setItem("crm-report-ask-open", askOpen ? "1" : "0"); }, [askOpen]);
  const [lastRow, setLastRow] = useState(() => sessionStorage.getItem(LAST_ROW_KEY) || "");
  const gridRef = useRef(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { try { sessionStorage.setItem(STORE_KEY, JSON.stringify(applied)); } catch { /* ignore */ } }, [applied]);

  const optionsQ = useQuery({ queryKey: ["report-options"], queryFn: async () => (await api.get("/report/options")).data });
  const resultQ = useQuery({
    queryKey: ["report-query", applied],
    queryFn: async () => (await api.post("/report/query", { ...applied, limit: 1000 })).data,
    keepPreviousData: true,
  });
  const savedQ = useQuery({ queryKey: ["report-saved"], queryFn: async () => (await api.get("/report/saved")).data, enabled: isSA });
  const saveMut = useMutation({
    mutationFn: async () => (await api.post("/report/saved", { name: saveName, filters: f })).data,
    onSuccess: () => { setSaveName(""); qc.invalidateQueries({ queryKey: ["report-saved"] }); toast.success("Report saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/report/saved/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["report-saved"] }),
  });
  const [exporting, setExporting] = useState(false);
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.post("/report/export", applied, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `report_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); } finally { setExporting(false); }
  };

  const opts = optionsQ.data || {};
  const res = resultQ.data;
  const hasArchive = (opts.archive_rows || 0) > 0;
  // Coming back from an order: scroll the Excel to the row you left and highlight it.
  useEffect(() => {
    if (!lastRow || !res?.rows?.length) return;
    const el = gridRef.current?.querySelector(`[data-testid="rpt-row-${lastRow}"]`);
    if (el) el.scrollIntoView({ block: "center" });
    const tmr = setTimeout(() => { setLastRow(""); sessionStorage.removeItem(LAST_ROW_KEY); }, 4000);
    return () => clearTimeout(tmr);
  }, [res, lastRow]);
  const labelOf = useMemo(() => ({ years: "Year", catalog_number: "Cat #", front_cover_code: "Front code", rear_cover_code: "Rear code", f_clamp_code: "F-Clamp", r_clamp_code: "R-Clamp", brand: "Brand", model: "Model", trim: "Trim", color: "Color", logo: "Logo", spacer: "Spacer", rim_size: "Rim size", country: "Country", state: "State / Prov", status: "Stage", source: "Source" }), []);
  const activeChips = useMemo(() => {
    const chips = [];
    Object.entries(applied).forEach(([k, v]) => {
      if (Array.isArray(v) && v.length) v.forEach((x) => chips.push({ k, v: x, text: `${labelOf[k] || k}: ${k === "status" ? (STAGE_LABEL[x] || x) : x}` }));
      else if (k === "year_from" && (v || applied.year_to)) chips.push({ k: "year", text: `Year ${v && applied.year_to ? (v === applied.year_to ? v : `${v}–${applied.year_to}`) : v ? `≥ ${v}` : `≤ ${applied.year_to}`}` });
      else if (k === "year_to") { /* handled with year_from */ }
      else if (k === "po" && v) chips.push({ k, text: `PO # ${v}` });
      else if (["city", "customer"].includes(k) && v) chips.push({ k, text: `${k}: ${v}` });
      else if (k === "text" && v) chips.push({ k, text: `“${v}”` });
      else if (k === "delivery" && v) chips.push({ k, text: `Delivery: ${v}` });
      else if (k === "include_cancelled" && v === false) chips.push({ k, text: "Cancelled excluded" });
      else if (k === "dataset" && v && v !== "both") chips.push({ k, text: v === "archive" ? "Old sheet only" : "Live orders only" });
    });
    return chips;
  }, [applied, labelOf]);
  const removeChip = (c) => {
    const next = { ...applied };
    if (c.k === "year") { next.year_from = ""; next.year_to = ""; }
    else if (Array.isArray(next[c.k])) next[c.k] = next[c.k].filter((x) => x !== c.v);
    else next[c.k] = EMPTY[c.k];
    setF(next); setApplied(next);
  };
  const run = () => setApplied(f);
  const reset = () => { setF(EMPTY); setApplied(EMPTY); };
  const applyFromAsk = (filters) => { const nf = { ...EMPTY, ...filters }; setF(nf); setApplied(nf); };
  const openOrder = (id) => { sessionStorage.setItem(LAST_ROW_KEY, id); navigate(`/order/${id}`, { state: { from: "report" } }); };
  const fmt = (k, v) => {
    if (v === null || v === undefined || v === "") return "";
    if (k === "created_at" || k === "fulfilled_at") return String(v).slice(0, 10);
    if (k === "status") return STAGE_LABEL[v] || v;
    if (k === "country") return v === "USA" ? "🇺🇸 USA" : v === "Canada" ? "🇨🇦 Canada" : v;
    if (k === "source") return v === "old_sheet" ? "old sheet" : v;
    if (k === "delivery") return v === "pickup" ? "🏬 pick-up" : "🚚 shipping";
    return String(v);
  };
  const groupLabel = (opts.group_by || []).find((g) => g.key === res?.group_by)?.label || res?.group_by;
  const advancedCount = ADVANCED_KEYS.reduce((n, k) => n + (Array.isArray(f[k]) ? f[k].length : f[k] ? 1 : 0), 0) + (f.include_cancelled === false ? 1 : 0);

  return (
    <div className="p-6" data-testid="report-page">
      <div className={`grid grid-cols-1 gap-5 items-start ${askOpen ? "xl:grid-cols-[1fr_340px]" : ""}`}>
        <div className="space-y-4 min-w-0">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2"><ChartColumn size={26} /> {t("Report")}</h1>
              <p className="text-sm text-slate-600 mt-1">Pick what you're looking for — every matching order line comes back as a spreadsheet with all its info and quick photo access.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={reset} data-testid="rpt-reset"><RotateCcw size={14} className="mr-1" /> Reset</Button>
              <Button size="sm" onClick={run} data-testid="rpt-run" className="bg-slate-900 hover:bg-black text-white"><Search size={14} className="mr-1" /> Run report</Button>
            </div>
          </div>

          {/* ---- Filters (+ "More options": save · export · import) ---- */}
          <FiltersV2
            f={f} set={set} opts={opts} run={run} advOpen={advOpen} setAdvOpen={setAdvOpen} moreOpen={moreOpen} setMoreOpen={setMoreOpen}
            hasArchive={hasArchive} isSA={isSA} advancedCount={advancedCount}
            tools={isSA && (
              <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(15rem,1fr))]" data-testid="rpt-tools">
                <div className="space-y-1.5 min-w-0" data-testid="rpt-tools-save">
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Save this report</div>
                  <div className="flex items-center gap-2">
                    <Input placeholder="Name this report…" value={saveName} onChange={(e) => setSaveName(e.target.value)} className="h-8 min-w-0 flex-1 text-xs" data-testid="rpt-save-name" />
                    <Button size="sm" variant="outline" className="h-8 shrink-0" disabled={!saveName.trim() || saveMut.isPending} onClick={() => saveMut.mutate()} data-testid="rpt-save"><Save size={14} className="mr-1" /> Save</Button>
                  </div>
                  {(savedQ.data || []).length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid="rpt-saved">
                      {savedQ.data.map((s) => (
                        <span key={s.id} className="inline-flex items-center rounded-md border border-slate-300 bg-white text-xs overflow-hidden">
                          <button type="button" className="px-2.5 py-1 hover:bg-slate-100 font-medium" onClick={() => applyFromAsk(s.filters)} data-testid={`rpt-saved-${s.id}`}>{s.name}</button>
                          <button type="button" className="px-1.5 py-1 border-l border-slate-200 text-slate-400 hover:text-red-600 hover:bg-red-50" title="Delete" onClick={() => { if (window.confirm(`Delete saved report "${s.name}"?`)) delMut.mutate(s.id); }} data-testid={`rpt-saved-del-${s.id}`}><Trash2 size={12} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-1.5 min-w-0" data-testid="rpt-tools-export">
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Export</div>
                  <Button size="sm" variant="outline" disabled={exporting || !res} onClick={exportXlsx} data-testid="rpt-export" className="h-8 w-full justify-start border-emerald-600 text-emerald-700 hover:bg-emerald-50"><Download size={14} className="mr-1" /> {exporting ? "Exporting…" : "Export Excel (current result)"}</Button>
                </div>
                <div className="space-y-1.5 min-w-0" data-testid="rpt-tools-import">
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-700">Import · data sources</div>
                  <Button size="sm" variant="outline" onClick={() => setArchiveOpen(true)} data-testid="rpt-archive-btn" className="h-8 w-full justify-start border-emerald-600 text-emerald-700 hover:bg-emerald-50" title="Import an old worksheet (Google Sheet or Excel/CSV) and set the Drive photo folder">
                    <FileSpreadsheet size={14} className="mr-1" /> Import Excel / Google Sheet · Drive link{hasArchive ? <span className="ml-auto text-[10px] text-slate-500 tabular-nums">{opts.archive_rows} rows</span> : null}
                  </Button>
                </div>
              </div>
            )}
          />

          {/* ---- Active chips ---- */}
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid="rpt-chips">
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mr-1">Filters</span>
            {activeChips.length === 0 ? (
              <span className="text-xs text-slate-400">No filters — showing every order.</span>
            ) : activeChips.map((c, i) => (
              <button key={i} type="button" onClick={() => removeChip(c)} className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white text-[11px] px-2.5 py-1 hover:bg-slate-700" data-testid={`rpt-chip-${i}`}>
                {c.text} <X size={11} />
              </button>
            ))}
          </div>

          {/* ---- Summary strip ---- */}
          {(() => {
            const summary = (
              <>
                <span className="font-display text-2xl font-bold tabular-nums" data-testid="rpt-count">{resultQ.isLoading ? "…" : (res?.count ?? 0)}</span>
                <span className="text-slate-600">matching order{res?.count === 1 ? "" : "s"}</span>
                {res?.archive_count > 0 && <span className="text-xs text-slate-500" data-testid="rpt-count-split">· {res.orders_count} live + <span className="font-semibold text-emerald-700">{res.archive_count} old sheet</span></span>}
                {res?.truncated && <span className="text-[11px] text-amber-700">· showing the first {res.rows.length}{isSA ? " — export for the full list" : ""}</span>}
                {res?.groups && (
                  <span className="flex flex-wrap items-center gap-1.5 ml-3" data-testid="rpt-groups">
                    <span className="text-[11px] font-mono uppercase tracking-widest text-slate-500">by {groupLabel}:</span>
                    {res.groups.slice(0, 12).map((g) => (
                      <span key={g.key} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs">
                        <span className="font-mono">{res.group_by === "status" ? (STAGE_LABEL[g.key] || g.key) : g.key}</span>
                        <span className="font-semibold tabular-nums">{g.count}</span>
                        <span className="text-slate-400 tabular-nums">{g.pct}%</span>
                      </span>
                    ))}
                    {res.groups.length > 12 && <span className="text-xs text-slate-400">+{res.groups.length - 12} more</span>}
                  </span>
                )}
              </>
            );
            return (
              <>
                <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="rpt-summary">{summary}</div>
                {/* ---- Excel-style grid (full screen · column filters · show/hide columns) ---- */}
                <ReportGrid cols={COLS} rows={res?.rows || []} loading={resultQ.isLoading} count={res?.count ?? 0} fmt={fmt} lastRow={lastRow} gridRef={gridRef} onOpenOrder={openOrder} onPeek={setPeek} summary={summary} />
              </>
            );
          })()}
        </div>

        {askOpen ? (
          <AskPanel applied={applied} onApply={applyFromAsk} onClose={() => setAskOpen(false)} />
        ) : (
          <button
            type="button"
            onClick={() => setAskOpen(true)}
            className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-violet-600 text-white shadow-lg shadow-violet-600/30 pl-3 pr-4 h-11 text-sm font-medium hover:bg-violet-700 hover:-translate-y-0.5 transition-[background-color,transform]"
            title="Ask the report — voice or text"
            data-testid="rpt-ask-fab"
          >
            <Sparkles size={16} /> Ask the report
          </button>
        )}
      </div>
      <PhotoPeek row={peek} onClose={() => setPeek(null)} onOpenOrder={() => { const id = peek.id; setPeek(null); openOrder(id); }} />
      {isSA && <ArchiveDialog open={archiveOpen} onClose={() => setArchiveOpen(false)} />}
    </div>
  );
}
